/*
 * Keeps the cart's free-gift lines and the gift discount code in sync with
 * what the shopper has actually earned. Tier rules come from the Free Gift
 * Tiers widget (snippets/free-gift-tiers.liquid pushes one entry per widget
 * onto window.freeGiftTiers before this file loads).
 *
 * WHAT COUNTS AS "EARNED"
 * ---------------------------------------------------------------------------
 * Only what is in the cart: for the widget's host product, the sum of
 * (loads/packs of the line's variant) x (line quantity). A tier with no gift
 * product configured stays purely visual.
 *
 * GIFT LINES
 * ---------------------------------------------------------------------------
 * Each gift line carries the hidden property `_free_gift: "<tier>"` and is
 * kept at quantity 1, one line per tier.
 *
 * MAKING GIFTS FREE (DISCOUNT CODES)
 * ---------------------------------------------------------------------------
 * Each tier can have a Buy X Get Y discount code (widget setting "Discount
 * code"). Only the highest earned tier's code is applied — Shopify won't let
 * one purchase qualify two Buy X Get Y codes — so that code must cover every
 * gift up to its tier. Shopify checks the purchase itself, so the property
 * alone never makes anything free.
 *
 * A gift line is never left in the cart at full price: if a gift isn't $0
 * after the codes are applied, it's removed, and that tier isn't re-added
 * until the cart changes (sessionStorage, keyed on the non-gift lines).
 *
 * CUSTOMER CODES
 * ---------------------------------------------------------------------------
 * A customer's own code (e.g. from an email link) is tried alongside the
 * gift code. If Shopify applies both, both stay. If not, the free gifts win:
 * the customer's code is dropped quietly, with no message and no switching
 * back. Only if the gifts still can't be made free does their code return.
 * The outcome is remembered per cart in localStorage. Codes typed at checkout
 * can't be seen from the theme, which is why the gift section carries a
 * "can't be combined" note.
 *
 * EVERY PAGE
 * ---------------------------------------------------------------------------
 * The product page saves its rules to localStorage; the cart drawer loads
 * this file on every page and uses the saved rules there. Saved rules may
 * add gifts only when discount codes are configured, because Shopify then
 * decides whether a gift is actually free; without codes they only remove.
 *
 * WHEN THIS RUNS
 * ---------------------------------------------------------------------------
 * On page load, and on every PUB_SUB_EVENTS.cartUpdate (published by the
 * theme after add-to-cart and quantity changes). After writing it publishes
 * its own cartUpdate (source 'free-gift-cart', ignored here) so the drawer
 * and cart page repaint. A `syncing` flag queues one re-run for events that
 * arrive mid-sync.
 */
(function () {
  // Both the widget and the cart drawer include this file on product pages.
  if (window.__freeGiftCartLoaded) return;
  window.__freeGiftCartLoaded = true;

  var GIFT_PROPERTY_KEY = '_free_gift';
  var RULES_KEY = 'freeGiftTiers:v1';
  var CHOICE_KEY = 'freeGiftChoice:v1';
  var SUPPRESS_KEY = 'freeGiftSuppressed:v1';
  var RULES_MAX_AGE_MS = 30 * 24 * 60 * 60 * 1000;

  function readJSON(storage, key) {
    try {
      return JSON.parse(storage.getItem(key));
    } catch (e) {
      return null;
    }
  }

  function writeJSON(storage, key, value) {
    try {
      storage.setItem(key, JSON.stringify(value));
    } catch (e) {}
  }

  var liveWidgets = (window.freeGiftTiers || [])
    .map(function (entry) {
      var qtyMapEl = document.getElementById(entry.qtyMapId);
      var productsMapEl = document.getElementById(entry.productsMapId);
      if (!qtyMapEl || !productsMapEl) return null;
      try {
        var giftProducts = JSON.parse(productsMapEl.textContent);
        if (!Object.keys(giftProducts).length) return null;
        return {
          hostProductId: entry.hostProductId,
          qtyByVariantId: JSON.parse(qtyMapEl.textContent),
          giftProducts: giftProducts,
          // "Gifts with Autoship only": only subscription lines count.
          subscriptionOnly: !!entry.subscriptionOnly,
        };
      } catch (e) {
        return null;
      }
    })
    .filter(Boolean);

  var liveRules = liveWidgets.length > 0;
  // Pack discounts from the quantity picker (snippets/variant-qty-picker.liquid).
  var livePacks =
    window.packDiscounts && window.packDiscounts.tiers && window.packDiscounts.tiers.length ? window.packDiscounts : null;
  var saved = readJSON(localStorage, RULES_KEY);
  var fresh = saved && Date.now() - saved.savedAt <= RULES_MAX_AGE_MS ? saved : null;
  var widgets = liveRules ? liveWidgets : fresh && Array.isArray(fresh.widgets) ? fresh.widgets : [];
  // A product page with the gift widget but no pack discounts turns them off.
  var packs = livePacks || (liveRules ? null : (fresh && fresh.packs) || null);
  // Only on the theme that set them up (split tests share this browser storage).
  var themeId = window.Shopify && Shopify.theme && Shopify.theme.id;
  if (packs && String(packs.themeId) !== String(themeId)) packs = null;
  if (liveRules || livePacks) writeJSON(localStorage, RULES_KEY, { savedAt: Date.now(), widgets: widgets, packs: packs });
  if (!widgets.length && !packs) return;

  var tierConfig = {};
  widgets.forEach(function (widget) {
    Object.keys(widget.giftProducts).forEach(function (tier) {
      tierConfig[tier] = widget.giftProducts[tier];
    });
  });

  var giftCodes = {};
  Object.keys(tierConfig).forEach(function (tier) {
    var code = (tierConfig[tier].code || '').trim();
    if (code) giftCodes[code.toUpperCase()] = true;
  });
  var packCodes = {};
  ((packs && packs.tiers) || []).forEach(function (t) {
    if (t.code) packCodes[String(t.code).trim().toUpperCase()] = true;
    if (t.otCode) packCodes[String(t.otCode).trim().toUpperCase()] = true;
  });
  var usesCodes = Object.keys(giftCodes).length > 0 || Object.keys(packCodes).length > 0;
  var canAdd = liveRules || usesCodes;
  // Lets the drawer hide a not-yet-free gift line (it's about to be removed),
  // only when codes are configured and something here will remove it.
  if (usesCodes) document.documentElement.classList.add('free-gift-codes');

  // Codes this script manages (free gifts and pack discounts), as opposed to
  // a code the customer entered.
  // Retired gift / pack code names (GIFTTEST-…, and PK5-, PK10-, ASG3-, ASG6-
  // before they became AG-…), still in some carts: ours, so they get replaced,
  // not offered as the customer's.
  var RETIRED_CODES = /^(GIFTTEST|PK\d+|ASG\d)-/i;

  function isGiftCode(code) {
    var c = String(code).toUpperCase();
    return !!giftCodes[c] || !!packCodes[c] || RETIRED_CODES.test(c);
  }

  // The pack-discount codes the cart's single packs have reached: the tier is
  // set by all single packs together; its Autoship code covers subscription
  // lines and its One-Time code the rest. Empty when no tier is reached.
  function packCodesFor(cart) {
    if (!packs) return [];
    var ids = (packs.variantIds || []).map(String);
    var count = 0;
    var hasSub = false;
    var hasOne = false;
    cart.items.forEach(function (item) {
      if (isGiftLine(item) || ids.indexOf(String(item.variant_id)) === -1) return;
      count += item.quantity;
      if (item.selling_plan_allocation) hasSub = true;
      else hasOne = true;
    });
    var best = null;
    (packs.tiers || []).forEach(function (t) {
      if (count >= t.min && (!best || t.min > best.min)) best = t;
    });
    if (!best) return [];
    var codes = [];
    if (hasSub && best.code) codes.push(String(best.code).trim());
    if (hasOne && best.otCode) codes.push(String(best.otCode).trim());
    // Older setups had one code for both.
    if (hasOne && !best.otCode && best.code && !hasSub) codes.push(String(best.code).trim());
    return codes;
  }
  function packCodeFor(cart) {
    return packCodesFor(cart)[0] || null;
  }

  // Everything this script wants applied: the gift code and the pack codes.
  function systemCodes(cart, giftCode) {
    return [giftCode].concat(packCodesFor(cart)).filter(Boolean);
  }

  function normCodes(codes) {
    return codes
      .map(function (c) {
        return String(c).toUpperCase();
      })
      .sort()
      .join(',');
  }

  function cartCodes(cart) {
    return (cart.discount_codes || []).map(function (d) {
      return d.code;
    });
  }

  function customerCodesIn(cart) {
    return cartCodes(cart).filter(function (c) {
      return !isGiftCode(c);
    });
  }

  function loadChoice(token) {
    var choice = readJSON(localStorage, CHOICE_KEY);
    return choice && choice.token === token ? choice : null;
  }

  function saveChoice(token, choice, codes, sig) {
    writeJSON(localStorage, CHOICE_KEY, { token: token, choice: choice, codes: codes, sig: sig || null });
  }

  function clearChoice() {
    try {
      localStorage.removeItem(CHOICE_KEY);
    } catch (e) {}
  }

  function isGiftLine(item) {
    return !!(item.properties && item.properties[GIFT_PROPERTY_KEY]);
  }

  function cartSignature(cart) {
    return (
      cart.items
        .filter(function (item) {
          return !isGiftLine(item);
        })
        .map(function (item) {
          return item.variant_id + ':' + item.quantity;
        })
        .sort()
        .join(',') +
      '|' +
      cart.currency
    );
  }

  function loadSuppressed(sig) {
    var s = readJSON(sessionStorage, SUPPRESS_KEY);
    return s && s.sig === sig ? s.tiers : [];
  }

  function earnedTiers(cart) {
    var earned = {};
    widgets.forEach(function (widget) {
      var packs = 0;
      cart.items.forEach(function (item) {
        if (item.product_id !== widget.hostProductId) return;
        if (widget.subscriptionOnly && !item.selling_plan_allocation) return;
        var perUnit = widget.qtyByVariantId[String(item.variant_id)];
        if (perUnit) packs += perUnit * item.quantity;
      });
      Object.keys(widget.giftProducts).forEach(function (tier) {
        if (packs >= widget.giftProducts[tier].threshold) earned[tier] = true;
      });
    });
    return Object.keys(earned);
  }

  // tier -> the one line kept for it; extra lines for a tier are duplicates.
  function giftLines(cart) {
    var byTier = {};
    var duplicates = [];
    cart.items.forEach(function (item) {
      if (!isGiftLine(item)) return;
      var tier = item.properties[GIFT_PROPERTY_KEY];
      if (byTier[tier]) duplicates.push(item.key);
      else byTier[tier] = { key: item.key, quantity: item.quantity, free: item.final_line_price === 0 };
    });
    return { byTier: byTier, duplicates: duplicates };
  }

  // The code of the highest tier in `tiers` that has one.
  function codeForTiers(tiers) {
    var best = null;
    tiers.forEach(function (tier) {
      var config = tierConfig[tier];
      if (!config || !(config.code || '').trim()) return;
      if (!best || config.threshold > tierConfig[best].threshold) best = tier;
    });
    return best ? tierConfig[best].code.trim() : null;
  }

  var wrote = false;

  // While a sync runs, block clicks on the cart items (CSS in cart-drawer.liquid)
  // so a shopper's remove/quantity change can't be sent at the same time as a
  // gift update — two concurrent writes to one cart can lose one of them. The
  // class sits on <html> because the cart markup is re-rendered, and Dawn
  // clears its own disabled class, mid-sync.
  function setCartBusy(busy) {
    document.documentElement.classList.toggle('free-gift-busy', busy);
  }

  // Until this sync's result is on screen, the drawer's savings total may not
  // count the gifts yet (html.free-gift-pending hides it). Shorter than busy:
  // it ends as soon as the drawer shows the result, or after 4s at most.
  var pendingCap = null;
  function setSavingsPending(on) {
    document.documentElement.classList.toggle('free-gift-pending', on);
    clearTimeout(pendingCap);
    if (on) pendingCap = setTimeout(function () { setSavingsPending(false); }, 4000);
  }

  function postJSON(url, body) {
    wrote = true;
    return fetch(url, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
      body: JSON.stringify(body),
    }).then(function (r) {
      if (!r.ok) throw new Error('free-gift-cart: ' + url + ' returned ' + r.status);
      return r.json();
    });
  }

  // Brings the cart to exactly `targetTiers` gift lines and (when
  // `touchCodes`) exactly `targetCodes`. Resolves to the resulting cart.
  function applyTarget(cart, targetTiers, targetCodes, touchCodes) {
    var lines = giftLines(cart);
    var updates = {};
    Object.keys(lines.byTier).forEach(function (tier) {
      if (!tierConfig[tier]) return;
      var line = lines.byTier[tier];
      if (targetTiers.indexOf(tier) === -1) updates[line.key] = 0;
      else if (line.quantity !== 1) updates[line.key] = 1;
    });
    lines.duplicates.forEach(function (key) {
      updates[key] = 0;
    });

    var toAdd = !canAdd
      ? []
      : targetTiers.filter(function (tier) {
          return !lines.byTier[tier] && tierConfig[tier].available !== false;
        });
    var codesChange = touchCodes && normCodes(cartCodes(cart)) !== normCodes(targetCodes);

    if (!toAdd.length && !Object.keys(updates).length && !codesChange) {
      return Promise.resolve(cart);
    }

    // Removals and the discount code go first, adds last: a gift added to a
    // cart that already carries its code comes back $0 in the add response
    // itself, so no render (ours or another script's) ever shows it at full
    // price for a moment.
    var needUpdate = Object.keys(updates).length > 0 || codesChange;
    var body = { updates: updates };
    if (touchCodes) body.discount = targetCodes.join(',');

    return (needUpdate ? postJSON(window.routes.cart_update_url, body) : Promise.resolve(cart)).then(function (updated) {
      if (!toAdd.length) return updated;
      return postJSON(window.routes.cart_add_url, {
        items: toAdd.map(function (tier) {
          var properties = {};
          properties[GIFT_PROPERTY_KEY] = tier;
          return { id: tierConfig[tier].variantId, quantity: 1, properties: properties };
        }),
      }).then(function () {
        return fetch('/cart.js').then(function (r) {
          return r.json();
        });
      });
    });
  }

  // Never leave a gift at full price: remove any gift line that isn't $0
  // and don't re-add that tier until the rest of the cart changes.
  function enforceFree(cart) {
    var lines = giftLines(cart);
    var notFree = Object.keys(lines.byTier).filter(function (tier) {
      return tierConfig[tier] && !lines.byTier[tier].free;
    });
    if (!notFree.length) return Promise.resolve(cart);

    var sig = cartSignature(cart);
    writeJSON(sessionStorage, SUPPRESS_KEY, { sig: sig, tiers: loadSuppressed(sig).concat(notFree) });
    var keep = Object.keys(lines.byTier).filter(function (tier) {
      return notFree.indexOf(tier) === -1;
    });
    var code = codeForTiers(keep);
    return applyTarget(cart, keep, customerCodesIn(cart).concat(systemCodes(cart, code)), true);
  }

  var uiState = null;
  var syncing = false;
  var pendingResync = false;

  function reconcile(cart) {
    var earned = earnedTiers(cart);
    var sig = cartSignature(cart);
    var suppressed = loadSuppressed(sig);
    var giftTiers = earned.filter(function (tier) {
      return suppressed.indexOf(tier) === -1;
    });
    var customerCodes = customerCodesIn(cart);
    var stored = readJSON(localStorage, CHOICE_KEY);
    if (stored && stored.token !== cart.token) clearChoice();
    var choice = loadChoice(cart.token);
    uiState = null;

    // Discount codes not configured yet: manage the lines only.
    if (!usesCodes) return applyTarget(cart, earned, [], false);

    if (!earned.length && !packCodeFor(cart)) {
      if (choice) clearChoice();
      return applyTarget(cart, [], customerCodes, true);
    }

    var giftCode = codeForTiers(giftTiers);
    var ours = systemCodes(cart, giftCode);

    if (!customerCodes.length) {
      var choseGifts = choice && choice.choice === 'gifts' && choice.codes && choice.codes.length;
      if (choice && !choseGifts) clearChoice();
      return applyTarget(cart, giftTiers, ours, true)
        .then(enforceFree)
        .then(function (result) {
          if (!choseGifts) return result;
          if (Object.keys(giftLines(result).byTier).length) return result;
          // Their code was dropped for gifts Shopify won't make free: put the
          // code back rather than leave them with neither.
          saveChoice(cart.token, 'kept', choice.codes, sig);
          return applyTarget(result, [], choice.codes, true);
        });
    }

    // Gifts couldn't be made free with this code dropped (see above): keep it.
    // ('kept', not the old 'code' choice, so a cart saved before gifts-first
    // doesn't hold on to its code.)
    if (choice && choice.choice === 'kept' && choice.sig === sig && normCodes(choice.codes) === normCodes(customerCodes)) {
      return applyTarget(cart, [], customerCodes, true);
    }

    // Unknown whether this code stacks with the gifts: apply both and let
    // Shopify decide.
    return applyTarget(cart, giftTiers, customerCodes.concat(ours), true).then(function (result) {
      // Every code of ours must actually apply alongside the customer's.
      var oursApply = ours.every(function (code) {
        return (result.discount_codes || []).some(function (d) {
          return d.code.toUpperCase() === code.toUpperCase() && d.applicable;
        });
      });
      // Both must actually apply. When they can't combine, Shopify keeps
      // whichever saves more and marks the other not applicable.
      var customerApplies = (result.discount_codes || []).every(function (d) {
        return isGiftCode(d.code) || d.applicable;
      });
      if (ours.length && oursApply && customerApplies) {
        clearChoice();
        return enforceFree(result);
      }
      // They can't combine: the gifts win, and the customer's code goes.
      forgetLinkCode();
      saveChoice(cart.token, 'gifts', customerCodes);
      return applyTarget(result, giftTiers, ours, true)
        .then(enforceFree)
        .then(function (after) {
          if (Object.keys(giftLines(after).byTier).length) return after;
          saveChoice(cart.token, 'kept', customerCodes, sig);
          return applyTarget(after, [], customerCodes, true);
        });
    });
  }

  // A /discount/CODE link sets this cookie, which Shopify can re-apply (e.g.
  // at checkout) and swap the gift code back out.
  function forgetLinkCode() {
    var expire = 'discount_code=; path=/; expires=Thu, 01 Jan 1970 00:00:00 GMT';
    document.cookie = expire;
    document.cookie = expire + '; domain=' + location.hostname;
    document.cookie = expire + '; domain=.' + location.hostname.replace(/^www\./, '');
  }

  function syncGifts() {
    if (syncing) {
      pendingResync = true;
      return;
    }
    syncing = true;
    wrote = false;
    setCartBusy(true);

    fetch('/cart.js')
      .then(function (r) {
        return r.json();
      })
      .then(reconcile)
      .catch(function (e) {
        console.error(e);
      })
      .finally(function () {
        // Repaint even after a partial failure so the drawer shows what changed.
        // After writing, stay "busy" until the drawer has re-rendered with the
        // new cart — until then the old markup may still hold a mid-update
        // (not yet free) gift, which must keep its free look rather than blink.
        if (wrote) {
          awaitDrawerRender();
          publish(PUB_SUB_EVENTS.cartUpdate, { source: 'free-gift-cart' });
        } else if (!eventTimer && !awaitingItemsEl) {
          setCartBusy(false);
        }
        // Nothing changed: what's on screen is already right.
        if (!wrote && !eventTimer && !awaitingItemsEl) setSavingsPending(false);
        renderChoice();
        syncing = false;
        if (pendingResync) {
          pendingResync = false;
          syncGifts();
        } else if (wrote && verifyPasses < 2) {
          // Other cart scripts (e.g. shipping-protection.js) also write on
          // cartUpdate; a write of theirs racing ours can undo it. Re-check
          // once things settle — a sync with nothing to change writes nothing.
          verifyPasses++;
          setTimeout(syncGifts, 1200);
        }
      });
  }

  var verifyPasses = 0;
  var eventTimer = null;

  // The drawer re-renders by replacing its <cart-drawer-items> element, so a
  // new element means the fresh markup is in. Release then, or after 3s.
  var awaitingItemsEl = null;
  var releaseTimer = null;

  function awaitDrawerRender() {
    var itemsEl = document.querySelector('cart-drawer-items');
    if (!itemsEl) {
      setCartBusy(false);
      return;
    }
    awaitingItemsEl = itemsEl;
    clearTimeout(releaseTimer);
    releaseTimer = setTimeout(releaseAfterRender, 3000);
  }

  function releaseAfterRender() {
    awaitingItemsEl = null;
    clearTimeout(releaseTimer);
    if (!eventTimer) setSavingsPending(false);
    if (!syncing && !eventTimer) setCartBusy(false);
  }

  // Collapse bursts of cart events, and let other scripts reacting to the
  // same event finish their write before this one starts.
  function syncSoon() {
    verifyPasses = 0;
    setCartBusy(true);
    setSavingsPending(true);
    clearTimeout(eventTimer);
    eventTimer = setTimeout(function () {
      eventTimer = null;
      syncGifts();
    }, 300);
  }

  // ---- "Your code X can't be combined with free gifts" message ----

  var strings = {
    conflict: "Your code [code] can't be combined with free gifts.",
    useGifts: 'Add Free Gifts',
    usingGifts: 'Free gifts applied instead of code [code].',
    useCode: 'Use [code] instead',
  };
  var stringsEl = document.getElementById('FreeGiftStrings');
  if (stringsEl) {
    try {
      var parsed = JSON.parse(stringsEl.textContent);
      // Liquid's `t` filter HTML-escapes (e.g. ' becomes &#39;); decode
      // before the strings are used as plain text.
      var decoder = document.createElement('textarea');
      Object.keys(strings).forEach(function (key) {
        if (!parsed[key]) return;
        decoder.innerHTML = parsed[key];
        strings[key] = decoder.value;
      });
    } catch (e) {}
  }

  function onChoiceClick(button) {
    var state = uiState;
    if (!state) return;
    button.disabled = true;
    var wantGifts = state.type === 'conflict';
    if (wantGifts) {
      // An explicit choice overrides the "gift wasn't free, don't re-add"
      // record, which may be left from while their code was blocking it.
      try {
        sessionStorage.removeItem(SUPPRESS_KEY);
      } catch (e) {}
      // A /discount/CODE link also sets this cookie, which Shopify can
      // re-apply (e.g. at checkout) and swap the gift code back out.
      var expire = 'discount_code=; path=/; expires=Thu, 01 Jan 1970 00:00:00 GMT';
      document.cookie = expire;
      document.cookie = expire + '; domain=' + location.hostname;
      document.cookie = expire + '; domain=.' + location.hostname.replace(/^www\./, '');
    }
    saveChoice(state.token, wantGifts ? 'gifts' : 'code', state.codes);
    setCartBusy(true);
    // Switching to gifts drops the customer's code; switching back re-applies
    // it (and drops the gift code). The sync then fixes the gift lines.
    postJSON(window.routes.cart_update_url, { updates: {}, discount: wantGifts ? '' : state.codes.join(',') })
      .catch(function (e) {
        console.error(e);
      })
      .then(syncGifts);
  }

  function renderChoice() {
    var drawer = document.querySelector('cart-drawer');
    if (!drawer) return;
    var anchor = drawer.querySelector('.drawer__cart-items-wrapper');
    var existing = drawer.querySelector('.cart-drawer__gift-choice');
    var key = uiState ? uiState.type + ':' + normCodes(uiState.codes) : '';

    if (existing && anchor && existing.dataset.key === key && existing.previousElementSibling === anchor) return;
    if (existing) existing.remove();
    if (!uiState || !anchor) return;

    var codeText = uiState.codes.join(', ');
    var box = document.createElement('div');
    box.className = 'cart-drawer__gift-choice';
    box.dataset.key = key;
    box.setAttribute('role', 'status');

    var text = document.createElement('span');
    text.textContent = (uiState.type === 'conflict' ? strings.conflict : strings.usingGifts).replace('[code]', codeText);

    var button = document.createElement('button');
    button.type = 'button';
    button.className = 'cart-drawer__gift-choice-button';
    button.textContent = (uiState.type === 'conflict' ? strings.useGifts : strings.useCode).replace('[code]', codeText);
    button.addEventListener('click', function () {
      onChoiceClick(button);
    });

    box.appendChild(text);
    box.appendChild(button);
    anchor.insertAdjacentElement('afterend', box);
  }

  // The drawer's contents are replaced on every cart update; put the message
  // back whenever that happens. renderChoice() makes no change when the
  // message is already right, so this can't loop.
  var drawerEl = document.querySelector('cart-drawer');
  if (drawerEl && window.MutationObserver) {
    new MutationObserver(function () {
      if (awaitingItemsEl && document.querySelector('cart-drawer-items') !== awaitingItemsEl) releaseAfterRender();
      renderChoice();
    }).observe(drawerEl, { childList: true, subtree: true });
  }

  subscribe(PUB_SUB_EVENTS.cartUpdate, function (event) {
    if (event.source === 'free-gift-cart') return;
    syncSoon();
  });

  // Skip the page-load cart read off the product page unless there's
  // something to check: a gift line in the drawer, or a remembered choice.
  if (liveRules || document.querySelector('.cart-drawer__gifts-tbody') || readJSON(localStorage, CHOICE_KEY)) {
    syncGifts();
  }
})();
